import React, { useState, useEffect, memo, Suspense } from 'react';
import {
  Layout, Menu, Typography, Table, Input, Select, Button,
  Space, Tag, Form, Badge, Pagination, Modal, DatePicker, message, Dropdown
} from 'antd';
import {
  UserOutlined, EnvironmentOutlined, MenuFoldOutlined,
  MenuUnfoldOutlined, SearchOutlined, ReloadOutlined,
  CaretDownOutlined, CaretRightOutlined, LogoutOutlined,
  DashboardOutlined, TeamOutlined, BookOutlined,
  SettingOutlined, CreditCardOutlined, KeyOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment';
import '../styles/Dashboard.css';
import {
  getAgentAccounts, createAgentAccount, updateAgentAccount, deleteAgentAccount
} from '../api/account';
import { getErrorMessage } from '../utils/errorHandler';
import { logout, changePassword } from '../api/auth';
import RegionSelector from '../components/RegionSelector';
import { setCurrentRegion, getCurrentRegion } from '../api/region';
import { DealerProvider } from '../context/DealerContext';
import performanceMonitor from '../utils/performanceMonitor';
import LazyWrapper from '../components/LazyWrapper';

// 懒加载组件
const AgencyAccountContent = React.lazy(() => import('../components/AgencyAccountContent'));
const InspectorAccountContent = React.lazy(() => import('../components/InspectorAccountContent'));
const TrialAccount = React.lazy(() => import('../components/TrialAccount'));
const RegularAccount = React.lazy(() => import('../components/RegularAccount'));
const CardConsumptionRecords = React.lazy(() => import('../components/CardConsumptionRecords'));

const { Header, Content, Sider, Footer } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const Dashboard = memo(() => {
  // 侧边栏状态
  const [collapsed, setCollapsed] = useState(false);
  
  // 账号数据状态
  const [agentAccounts, setAgentAccounts] = useState([]);
  const [agentStats, setAgentStats] = useState({
    total: 20,
    active: 5,
    disabled: 1,
    remaining: 15
  });
  const [loading, setLoading] = useState(false);
  
  // 表单和分页状态
  const [agentSearchForm] = Form.useForm();
  const [agentPagination, setAgentPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  
  // 模态框状态
  const [showAgentCreateModal, setShowAgentCreateModal] = useState(false);
  const [showAgentEditModal, setShowAgentEditModal] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);
  const [currentAgent, setCurrentAgent] = useState(null);
  
  // 用户信息状态
  const [userInfo, setUserInfo] = useState({
    fullName: '',
    location: '',
    regionId: null,
    role: null
  });
  
  // 地区选择相关状态
  const [showRegionSelector, setShowRegionSelector] = useState(false);
  const [selectedRegions, setSelectedRegions] = useState([]);
  
  // 当前选中的菜单：刷新/首次进入默认展示“正式账号”
  const [selectedMenu, setSelectedMenu] = useState('formal');

  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [passwordForm] = Form.useForm();
  
  const navigate = useNavigate();
  const location = useLocation();
  const canManageAgents = userInfo.role === 'DEALER' || userInfo.role === 'ADMIN';

  // 初始化数据
  useEffect(() => {
    getUserInfo(); // 先获取用户信息，包括地区信息
  }, []);

  // 当用户信息或分页信息变化时，获取相应的数据
  useEffect(() => {
    if (userInfo.regionId) { // 只有当地区ID存在时才获取数据
      if (selectedMenu === 'agency' && canManageAgents) {
        fetchAgentAccounts();
      } else if (selectedMenu === 'inspector') {
        // 如果有督学账号的获取函数，也应该在这里调用
      }
    }
  }, [agentPagination.current, agentPagination.pageSize, selectedMenu, userInfo.regionId, userInfo.role]);

  // 根据当前URL设置选中的菜单
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/account/agency')) {
      if (canManageAgents) {
        setSelectedMenu('agency');
      } else {
        setSelectedMenu('inspector');
        navigate('/account/supervisor', { replace: true });
      }
    } else if (path.includes('/account/supervisor')) {
      setSelectedMenu('inspector');
    } else if (path.includes('/account/students/trial')) {
      setSelectedMenu('trial');
    } else if (path.includes('/account/students/formal')) {
      setSelectedMenu('formal');
    } else if (path.includes('/account/card')) {
      setSelectedMenu('card');
    } else if (path === '/dashboard') {
      setSelectedMenu('formal');
      navigate('/account/students/formal', { replace: true });
    }
  }, [location.pathname, navigate, userInfo.role]);

  // 获取用户信息
  const getUserInfo = async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        setUserInfo({
          fullName: user.fullName || '未知用户',
          location: '加载中...', // 初始化时设置为加载中
          regionId: null,
          role: user.role || null
        });
        
        // 从后端获取用户当前选择的地区信息
        fetchCurrentRegion();
      }
    } catch (error) {
      console.error('获取用户信息失败:', error);
    }
  };
  
  // 获取用户当前选择的地区信息
  const fetchCurrentRegion = async () => {
    try {
      const region = await getCurrentRegion();
      if (region) {
        setUserInfo(prev => ({
          ...prev,
          location: region.regionPath || '未设置地区',
          regionId: region.regionId // 保存regionId到状态中
        }));
      } else {
        // 用户尚未设置地区，显示友好提示
        setUserInfo(prev => ({
          ...prev,
          location: '点击选择地区',
          regionId: null
        }));
      }
    } catch (error) {
      // 如果是权限问题或其他错误，显示错误状态
      if (error.response && error.response.status === 403) {
        console.warn('用户权限不足，无法访问地区信息');
        setUserInfo(prev => ({
          ...prev,
          location: '权限不足',
          regionId: null
        }));
      } else {
        console.error('获取用户地区信息失败:', error);
        setUserInfo(prev => ({
          ...prev,
          location: '获取地区失败',
          regionId: null
        }));
      }
    }
  };

  // 显示地区选择器
  const showRegionSelectorModal = () => {
    setShowRegionSelector(true);
  };

  // 隐藏地区选择器
  const hideRegionSelector = () => {
    setShowRegionSelector(false);
  };

  // 选择地区后的处理
  const handleRegionSelect = async (region) => {
    try {
      // 保存到后端
      await setCurrentRegion({
        regionId: region.district.id // 使用区/县级ID
      });
      
      // 更新显示和本地状态
      setUserInfo(prev => ({
        ...prev,
        location: region.path,
        regionId: region.district.id // 更新本地存储的regionId
      }));
      
      // 根据当前选中的菜单刷新相应的数据
      if (selectedMenu === 'agency') {
        fetchAgentAccounts();
      } else if (selectedMenu === 'inspector') {
        // 如果有督学账号的获取函数，也应该在这里调用
      } else if (selectedMenu === 'trial' || selectedMenu === 'formal') {
        // 如果有学生账号的获取函数，也应该在这里调用
      }
      
      message.success(`已选择地区: ${region.path}`);
      hideRegionSelector(); // 关闭地区选择器
    } catch (error) {
      console.error('保存地区信息失败:', error);
      message.error('保存地区信息失败');
    }
  };

  
  // 获取代理账号列表
  const fetchAgentAccounts = async () => {
    setLoading(true);
    try {
      const params = {
        page: agentPagination.current - 1, // 后端页码从0开始
        size: agentPagination.pageSize,
        ...agentSearchForm.getFieldsValue(),
        regionId: userInfo.regionId // 明确传递地区ID
      };
      
      const response = await getAgentAccounts(params);
      setAgentAccounts(response.data.content || []);
      setAgentPagination({
        ...agentPagination,
        total: response.data.totalElements || 0
      });
      setLoading(false);
    } catch (error) {
      console.error('获取代理账号列表失败:', error);
      message.error('获取代理账号列表失败');
      setLoading(false);
    }
  };

  
  // 代理账号搜索
  const handleAgentSearch = (values) => {
    setAgentPagination({
      ...agentPagination,
      current: 1,
    });
    fetchAgentAccounts();
  };

  // 重置代理账号搜索
  const handleAgentReset = () => {
    agentSearchForm.resetFields();
    setAgentPagination({
      ...agentPagination,
      current: 1,
    });
    fetchAgentAccounts();
  };

  // 处理代理账号分页变化
  const handleAgentPageChange = (page, pageSize) => {
    setAgentPagination({
      ...agentPagination,
      current: page,
      pageSize: pageSize
    });
  };


  // 用户下拉菜单项
  const userMenuItems = [
    {
      key: 'changePassword',
      icon: <KeyOutlined />,
      label: '修改密码',
      onClick: () => {
        setPasswordModalVisible(true);
        passwordForm.resetFields();
      }
    },
    {
      key: 'logout',
      danger: true,
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: logout
    }
  ];

  // 处理菜单点击
  const handleMenuClick = (menuKey) => {
    setSelectedMenu(menuKey);
    
    // 根据菜单项导航到相应页面
    // 注意：我们保持URL更新但不实际导航，因为所有内容都在同一个Dashboard组件中处理
    const url = getUrlByMenuKey(menuKey);
    if (url) {
      navigate(url, { replace: true });
    }
  };
  
  // 根据菜单键获取URL
  const getUrlByMenuKey = (menuKey) => {
    switch(menuKey) {
      case 'agency':
        return '/account/agency';
      case 'inspector':
        return '/account/supervisor';
      case 'trial':
        return '/account/students/trial';
      case 'formal':
        return '/account/students/formal';
      case 'card':
        return '/account/card';
      default:
        return '/dashboard';
    }
  };


  // 根据选中的菜单渲染不同的内容 - 使用懒加载和性能监控
  const renderWorkspaceContent = () => {
    return performanceMonitor.measureRender(`Dashboard-${selectedMenu}`, () => {
      switch (selectedMenu) {
        case 'agency':
          if (!canManageAgents) {
            return <div>无权限访问代理账号管理</div>;
          }
          return (
            <LazyWrapper>
              <AgencyAccountContent />
            </LazyWrapper>
          );
        case 'inspector':
          return (
            <LazyWrapper>
              <InspectorAccountContent />
            </LazyWrapper>
          );
        case 'trial':
          return (
            <LazyWrapper>
              <TrialAccount />
            </LazyWrapper>
          );
        case 'formal':
          return (
            <LazyWrapper>
              <RegularAccount />
            </LazyWrapper>
          );
        case 'card':
          return (
            <LazyWrapper>
              <CardConsumptionRecords />
            </LazyWrapper>
          );
        default:
          return <div>首页内容</div>;
      }
    });
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* 导航区：侧边栏 */}
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed} 
        theme="light"
        className="site-sider"
        width={200}
      >
        <div className="logo">
          <span className="logo-text">经销商后台</span>
          {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
            className: 'trigger',
            onClick: () => setCollapsed(!collapsed),
          })}
        </div>

        <Menu
          mode="inline"
          selectedKeys={[selectedMenu]}
          style={{ borderRight: 0 }}
          className="sidebar-menu"
        >
          <Menu.SubMenu 
            key="accountManagement" 
            icon={<TeamOutlined />} 
            title="账号管理"
          >
            {canManageAgents && (
              <Menu.Item key="agency" onClick={() => handleMenuClick('agency')}>
                代理账号
              </Menu.Item>
            )}
            <Menu.Item key="inspector" onClick={() => handleMenuClick('inspector')}>
              督学账号
            </Menu.Item>
          </Menu.SubMenu>
          
          <Menu.SubMenu 
            key="studentAccounts" 
            icon={<BookOutlined />} 
            title="学生账号"
          >
            <Menu.Item key="trial" onClick={() => handleMenuClick('trial')}>
              试用账号
            </Menu.Item>
            <Menu.Item key="formal" onClick={() => handleMenuClick('formal')}>
              正式账号
            </Menu.Item>
          </Menu.SubMenu>
          
          <Menu.Item 
            key="card" 
            icon={<CreditCardOutlined />}
            onClick={() => handleMenuClick('card')}
          >
            卡消耗记录
          </Menu.Item>
          
          {/* <Menu.Item 
            key="settings" 
            icon={<SettingOutlined />}
          >
            系统设置
          </Menu.Item> */}
        </Menu>
      </Sider>
      
      <Layout className="site-layout">
        {/* 导航区：顶部栏 */}
        <Header className="site-header">
          <div className="header-left">
            <span className="header-title">经销商后台</span>
          </div>
          <div className="header-right">
            <Space>
              <span 
                onClick={showRegionSelectorModal} 
                style={{ cursor: 'pointer' }}
              >
                <EnvironmentOutlined /> {userInfo.location}
              </span>
              <Dropdown menu={{items: userMenuItems}} placement="bottomRight" arrow>
                <span className="user-dropdown" style={{ cursor: 'pointer' }}>
                  <UserOutlined /> {userInfo.fullName} <CaretDownOutlined />
                </span>
              </Dropdown>
            </Space>
          </div>
        </Header>
        
        {/* 工作区 */}
        <Content className="site-content">
          {renderWorkspaceContent()}
        </Content>
        
        {/* 页脚区 */}
        <Footer style={{ textAlign: 'center' }}>
          数理阶梯 ©{new Date().getFullYear()} {process.env.REACT_APP_COPYRIGHT_HOLDER || '数理阶梯'} 版权所有
        </Footer>
      </Layout>


      {/* 地区选择器 */}
      <RegionSelector
        visible={showRegionSelector}
        onCancel={hideRegionSelector}
        onSelect={handleRegionSelect}
      />

      <Modal
        title="修改密码"
        open={passwordModalVisible}
        onCancel={() => setPasswordModalVisible(false)}
        onOk={async () => {
          try {
            const values = await passwordForm.validateFields();
            await changePassword(values.oldPassword, values.newPassword);
            message.success('密码修改成功，请重新登录');
            setPasswordModalVisible(false);
            logout();
          } catch (e) {
            if (e && e.errorFields) return;
            message.error('密码修改失败: ' + (e?.response?.data?.message || e?.response?.data || e?.message || '未知错误'));
          }
        }}
        okText="确认"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={passwordForm} layout="vertical">
          <Form.Item
            label="旧密码"
            name="oldPassword"
            rules={[{ required: true, message: '请输入旧密码' }]}
          >
            <Input.Password placeholder="请输入旧密码" />
          </Form.Item>
          <Form.Item
            label="新密码"
            name="newPassword"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '新密码至少6位' }
            ]}
          >
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>
          <Form.Item
            label="确认新密码"
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请再次输入新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的新密码不一致'));
                }
              })
            ]}
          >
            <Input.Password placeholder="请再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
});

Dashboard.displayName = 'Dashboard';

// 使用Context包装Dashboard
const DashboardWithProvider = () => (
  <DealerProvider>
    <Dashboard />
  </DealerProvider>
);

export default DashboardWithProvider;
